import { useRef } from "react"
import cx from "clsx"
import { useMutation, useQuery } from "@tanstack/react-query"
import { FormModuleErrorCode, type FormModuleDetail } from "shared"
import { AdminButton } from "@rawa/ui/AdminButton"
import { ErrorCallout } from "@rawa/ui/ErrorCallout"
import {
  ApiResponseError,
  isExpectedApiError,
} from "@rawa/api-client/ApiResponseError"
import { trackError } from "@rawa/telemetry"
import typography from "@rawa/ui/styles/typography.module.css"
import { fetchFormModuleTemplates, publishFormModule } from "../api"
import { AdminDialog } from "./AdminDialog"
import { SaveRefusal } from "./SaveRefusal"
import styles from "./PublishModuleDialog.module.css"

/**
 * Asks before publishing a module's saved changes, naming the session
 * templates it reaches (ADR-040): a change to the fields gives each a new
 * version; a change to the name or description alone replaces the current
 * version's, which those templates then show, and makes no version. Publishes
 * at the revision the canvas holds, so a copy someone has saved past is
 * refused, never published unseen. Cancel, Escape and the corner close all
 * keep editing.
 */
export function PublishModuleDialog({
  open,
  moduleId,
  moduleName,
  revision,
  change,
  firstPublish,
  onClose,
  onPublished,
}: {
  open: boolean
  moduleId: string
  moduleName: string
  revision: number
  change: "fields" | "wording"
  firstPublish: boolean
  onClose: () => void
  onPublished: (module: FormModuleDetail) => void
}) {
  const cancel = useRef<HTMLButtonElement>(null)
  const reach = useQuery({
    queryKey: ["form-module-templates", moduleId],
    queryFn: ({ signal }) => fetchFormModuleTemplates(moduleId, signal),
    enabled: open,
    // Read fresh each time it opens: the templates holding a module change
    // elsewhere, and this names what a publish is about to change.
    staleTime: 0,
    retry: false,
    meta: { source: "fetchFormModuleTemplates" },
  })
  const publish = useMutation({
    mutationFn: () => publishFormModule(moduleId, { revision }),
    onError: (err) => trackError(err, { source: "publishFormModule" }),
    onSuccess: (published) => onPublished(published),
  })

  const changedElsewhere =
    publish.error instanceof ApiResponseError &&
    publish.error.code === FormModuleErrorCode.FORM_MODULE_CHANGED
  const templates = reach.data?.sessionTemplates

  return (
    <AdminDialog
      open={open}
      onClose={onClose}
      labelledBy="publish-title"
      initialFocus={cancel}
      className={styles.dialog}
    >
      <h2 id="publish-title" className={cx(typography.h5, styles.title)}>
        Publish {moduleName}?
      </h2>
      {reach.isError && (
        <p className={styles.body}>
          Couldn&rsquo;t check which session templates use this module.
        </p>
      )}
      {templates && templates.length === 0 && (
        <p className={styles.body}>
          {change === "wording"
            ? "The new name and description replace the current version's. No session templates use this module."
            : firstPublish
              ? "Once published, it can be added to session templates."
              : "No session templates use this module, so none will change."}
        </p>
      )}
      {templates && templates.length > 0 && (
        <>
          <p className={styles.body}>
            {change === "wording"
              ? "The new name and description replace the current version's, so these session templates show them. No new version is made."
              : "These session templates will use the new version for notes started from now on. Notes already started keep the version they began with."}
          </p>
          <ul
            className={styles.templates}
            aria-label={
              change === "wording"
                ? "Session templates showing the new name"
                : "Session templates updated"
            }
          >
            {templates.map((template) => (
              <li key={template.id}>{template.name}</li>
            ))}
          </ul>
        </>
      )}
      {publish.error && (
        <ErrorCallout>
          {changedElsewhere ? (
            "Someone has changed this module since you opened it. Reload it to see their changes before publishing."
          ) : isExpectedApiError(publish.error) ? (
            <SaveRefusal error={publish.error} />
          ) : (
            "Could not publish the module. Please try again."
          )}
        </ErrorCallout>
      )}
      <div className={styles.actions}>
        <AdminButton variant="outline" ref={cancel} onClick={onClose}>
          Cancel
        </AdminButton>
        <AdminButton
          onClick={() => publish.mutate()}
          ariaDisabled={publish.isPending || !reach.isSuccess}
        >
          {publish.isPending ? "Publishing…" : "Publish"}
        </AdminButton>
      </div>
    </AdminDialog>
  )
}
